import { useState } from "react";
import { InlineHelp } from "@/components/common/InlineHelp";
import { ManufacturingModelsDictateMicButton } from "@/components/manufacturing/ManufacturingModelsDictateMicButton";
import { ghostBtnClass, inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import {
  markBuilderAssistantObservationFixed,
  uploadBuilderAssistantPhoto,
} from "@/lib/manufacturing/builderAssistantClient";
import { parseFixSpeech } from "@/lib/manufacturing/builderAssistantSpeech";
import { loadQcObservationPhotos } from "@/lib/manufacturing/qcChecklistPhotoRead";
import { QcStoredPhotoLink } from "@/components/manufacturing/qc/QcStoredPhotoLink";
import type { BuilderAssistantEvidence, BuilderAssistantObservation } from "@/lib/manufacturing/builderAssistantTypes";

export function BuilderAssistantObservationFix({
  observation,
  photos,
  assumedName,
  disabled,
  onChanged,
}: {
  observation: BuilderAssistantObservation;
  photos: BuilderAssistantEvidence[];
  assumedName: string;
  disabled: boolean;
  onChanged: () => void;
}) {
  const savedName = observation.fixed_by_name || assumedName;
  const [open, setOpen] = useState(false);
  const [description, setDescription] = useState(observation.fix_description);
  const [fixerName, setFixerName] = useState(savedName);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const fixed = Boolean(observation.fixed_at);

  async function save() {
    setBusy(true);
    setError(null);
    const parsed = parseFixSpeech(description, fixerName || assumedName);
    const res = await markBuilderAssistantObservationFixed({
      observation_id: observation.id,
      description: parsed.description,
      fixed_by_name: parsed.fixedByName,
    });
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    setDescription(parsed.description);
    setFixerName(parsed.fixedByName);
    setOpen(false);
    onChanged();
  }

  async function addPhoto(file: File) {
    setBusy(true);
    setError(null);
    const res = await uploadBuilderAssistantPhoto(observation.id, file, "fix");
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      return;
    }
    onChanged();
  }

  return (
    <div className="space-y-2 border-t border-gray-200 pt-2 dark:border-zinc-700">
      {fixed ? (
        <p className="text-xs text-gray-700 dark:text-zinc-300">
          Fixed{observation.fixed_by_name ? ` by ${observation.fixed_by_name}` : ""}
          {observation.fix_description ? ` — ${observation.fix_description}` : ""}
        </p>
      ) : (
        <p className="text-xs text-gray-500 dark:text-zinc-400">Not fixed yet</p>
      )}
      {photos.length > 0 ? (
        <QcStoredPhotoLink
          label={`Fix images (${photos.length})`}
          load={async () => {
            const listed = await loadQcObservationPhotos(observation.id, "fix");
            if (!listed.ok) return listed;
            return { ok: true, urls: listed.data.photos.map((photo) => photo.url) };
          }}
        />
      ) : null}
      {open ? (
        <div className="space-y-2">
          <label className="block text-xs font-medium text-gray-700 dark:text-zinc-300">
            Fixed by
            <input
              className={`${inputClass} mt-1 w-full`}
              value={fixerName}
              onChange={(event) => setFixerName(event.target.value)}
              disabled={disabled || busy}
            />
          </label>
          <ManufacturingModelsDictateMicButton
            extractLabel="Use this note"
            placeholder="What changed. Say fixed by and a name to record someone else."
            clearOnExtract
            disabled={disabled || busy}
            help={
              <InlineHelp glyph="!" label="How to name the fixer">
                Your name is already filled in. Say fixed by and a name to replace it. Example: replaced
                the nail. Fixed by Mike Torres.
              </InlineHelp>
            }
            onComplete={(transcript) => {
              const parsed = parseFixSpeech(transcript, fixerName || assumedName);
              setDescription(parsed.description);
              setFixerName(parsed.fixedByName);
            }}
          />
          <textarea
            className={`${inputClass} min-h-16 w-full`}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            disabled={disabled || busy}
            placeholder="What changed"
          />
          <div className="flex flex-wrap gap-2">
            <button type="button" className={ghostBtnClass} disabled={disabled || busy} onClick={() => void save()}>
              Save fix
            </button>
            <label className={`${ghostBtnClass} inline-flex cursor-pointer`}>
              Fix photo
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp"
                capture="environment"
                className="hidden"
                disabled={disabled || busy}
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  event.target.value = "";
                  if (file) void addPhoto(file);
                }}
              />
            </label>
          </div>
        </div>
      ) : (
        <button
          type="button"
          className={ghostBtnClass}
          disabled={disabled}
          onClick={() => {
            setDescription(observation.fix_description);
            setFixerName(observation.fixed_by_name || assumedName);
            setOpen(true);
          }}
        >
          {fixed ? "Update fix" : "Mark fixed"}
        </button>
      )}
      {error ? <p className="text-sm text-red-700 dark:text-red-300">{error}</p> : null}
    </div>
  );
}
